import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl, DECK_IMG, HERO_IMG } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";
import { getBootstrap } from "../../lib/bootstrap";
import { cache } from "../../lib/cache";
import SEO from "../../components/SEO";
import CTABand from "../../components/public/CTABand";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "decks", label: "Decks" },
  { key: "kitchens", label: "Kitchens" },
  { key: "bathrooms", label: "Bathrooms" },
  { key: "exterior", label: "Exterior" },
  { key: "additions", label: "Additions" },
];

const FALLBACK = [
  { id: "f1", title: "Composite deck build", category: "decks", image_path: DECK_IMG },
  { id: "f2", title: "Kitchen refresh", category: "kitchens", image_path: HERO_IMG },
  { id: "f3", title: "Primary bath remodel", category: "bathrooms", image_path: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1200&q=80" },
  { id: "f4", title: "Siding refresh", category: "exterior", image_path: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80" },
  { id: "f5", title: "Second-story addition", category: "additions", image_path: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80" },
  { id: "f6", title: "Architectural roof", category: "exterior", image_path: "https://images.unsplash.com/photo-1632823471406-4c5c7fe10475?auto=format&fit=crop&w=1200&q=80" },
];

export default function Gallery() {
  const c = useContent();
  const site = useSite();
  const [items, setItems] = useState(() => {
    const boot = getBootstrap("gallery");
    if (Array.isArray(boot) && boot.length > 0) return boot;
    const cached = cache.get("gallery");
    if (Array.isArray(cached) && cached.length > 0) return cached;
    return [];
  });
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/gallery").then((r) => {
      if (cancelled) return;
      if (Array.isArray(r.data)) {
        cache.set("gallery", r.data);
        setItems(r.data);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const display = useMemo(() => {
    const list = items.length > 0 ? items : FALLBACK;
    return cat === "all" ? list : list.filter((i) => i.category === cat);
  }, [items, cat]);

  return (
    <div data-testid="gallery-page">
      <SEO
        path="/gallery"
        title="Project Gallery — Decks, Kitchens, Baths & More"
        description="Real ATC Home Pro's projects across Southern York County, PA. Browse our finished work by category."
      />
      <section className="bg-atc-black text-white pt-20 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:4rem_4rem] opacity-30" />
        <div className="container-max relative">
          <div className="overline mb-4 text-atc-red">Gallery</div>
          <h1 className="h-display">The work.<br/>The proof.</h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl">A growing archive of ATC Home Pro's projects across Southern York County. Updated weekly.</p>
        </div>
      </section>

      <section className="section-pad bg-atc-paper">
        <div className="container-max">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCat(c.key)}
                data-testid={`filter-${c.key}`}
                className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 transition-all ${
                  cat === c.key ? "bg-atc-black text-white border-atc-black" : "bg-white text-atc-black border-atc-black hover:bg-atc-red hover:text-white hover:border-atc-red"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {display.length === 0 ? (
            <div className="text-center py-20 border-2 border-dashed border-atc-black/30">
              <p className="text-atc-muted">{pick(c, "gallery_page.empty_message", "No projects in this category yet.")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {display.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => setLightbox(item)}
                  data-testid={`gallery-item-${i}`}
                  className={`group relative block border-2 border-atc-black bg-atc-black overflow-hidden text-left ${
                    i % 5 === 0 ? "sm:col-span-2" : ""
                  }`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={fileUrl(item.image_path) || item.image_path} alt={item.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500 group-hover:scale-105" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-atc-black/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                    <div className="overline text-atc-red">{item.category || "Project"}</div>
                    {item.title && <h3 className="font-heading uppercase tracking-tight text-lg mt-1">{item.title}</h3>}
                    {item.caption && <p className="text-sm text-white/80 mt-1">{item.caption}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-atc-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <div className="relative max-w-5xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={fileUrl(lightbox.image_path) || lightbox.image_path} alt="" className="w-full h-full object-contain" />
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 bg-white text-atc-black p-2 hover:bg-atc-red hover:text-white" data-testid="close-lightbox">✕</button>
            {(lightbox.title || lightbox.caption) && (
              <div className="absolute bottom-0 left-0 right-0 bg-atc-black/80 text-white p-5">
                {lightbox.title && <h3 className="font-heading uppercase tracking-tight text-xl">{lightbox.title}</h3>}
                {lightbox.caption && <p className="text-sm text-white/80 mt-1">{lightbox.caption}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      <CTABand phone={site?.phone} />
    </div>
  );
}
